/**
 * OBWWizard — single configurable wizard for all MVP OBW journeys.
 *
 * Journey behaviour (required steps, autoFill, exitPolicy, allowedActors)
 * is driven by the JOURNEY_MATRIX in journeys.ts per ADR-021.
 *
 * Website analysis (autoFill='website-ai') fires after domain-verify and
 * results pre-fill Persona + Brand step renderers per ADR-023 §6.1.
 *
 * Step persistence: POST /portal/obw/:accountId/step/:stepId after each
 * forward navigation. Rollback on exit for 'rollback' journey configs
 * (sign-up journey, OBW-012 rule).
 *
 * ⛔ THE ENGINE HOLDS NO `fetch` — ADR-107 §2.4, AND THIS IS THE BUILD'S LOAD-BEARING
 * STRUCTURAL CHANGE.
 *
 * ADR-076 said the mount owns auth and the engine owns none. The engine then made
 * its own `fetch` calls anyway — `credentials: 'include'`, no `Authorization`
 * header — while `/portal/obw/*` sits behind the Cognito `clientAuthorizer`, which
 * reads `Authorization` and nothing else. There is no cookie auth path on this
 * platform. Probed live on 22 July 2026, all three engine calls returned 401:
 * step-persistence, rollback, and website analysis. For months.
 *
 * The damage was invisible because each call was best-effort inside a bare
 * `try {} catch {}`: a refused write and a successful one produce identical
 * silence. So PR #311's resume restoration and PR #312's answer persistence were
 * INERT in production (their probes called the API directly with a Bearer the
 * real wizard never sent), and PR #313's rollback — the half of the domain-mint
 * PAIR that releases an abandoned hostname — never fired, leaving stranded
 * `pending` Domain rows that lock a visitor out of their own domain via the 409
 * uniqueness guard.
 *
 * One defect, not three: AN ENGINE THAT PERFORMS ITS OWN I/O WHILE DISCLAIMING
 * KNOWLEDGE OF AUTH IS A CONTRADICTION, AND THE CONTRADICTION RESOLVES SILENTLY
 * IN FAVOUR OF "UNAUTHENTICATED".
 *
 * Every server interaction is therefore a PORT supplied by the mount as a prop
 * (`onPersistStep`, `onRollback`, `onAnalyse`, `onCreateDomain`). A mount that
 * owns auth cannot forget to attach it, because attaching it is the only way it
 * can implement the port at all; and an engine with no `fetch` cannot make an
 * unauthenticated call. The defect class is closed by construction rather than by
 * vigilance — which matters, because vigilance had already failed six times.
 *
 * CONTEXT-SEVERANCE (ADR-076 §4): the engine carries NO knowledge of auth,
 * session, route, or portal. It emits results to its mount and never routes.
 *   - L1 mount-owns-exit: required onComplete(result)/onExit(reason) props.
 *     There is NO useNavigate / navigate() anywhere in the engine or steps —
 *     the mount (subscriber route, staff modal, future partner mount) decides
 *     where completion and exit go.
 *   - L2 no native dialogs: exit confirmation is an in-app panel inside
 *     .obw-overlay (never window.confirm / window.close). The mount raises any
 *     toast in its onComplete/onExit handler (it owns the portal's toast).
 *   - L5 no mount-context sniffing: no `if (journeyId === …)` branching here;
 *     per-journey behaviour is data-driven (journey config / content maps).
 */
import { useState, useCallback, useEffect } from 'react';
import { JOURNEY_MATRIX, MASTER_STEP_LABELS, type JourneyId, type ObwStepId } from './journeys';
import { assertTrust } from './assess';
import { firstIncompleteRequiredStep, type ObwEvidence } from './validators';
import type { ResidencyContext } from './residency';
import type { PaymentPort } from './payment';
import { StepResidency } from './steps/StepResidency';

import { StepWelcome } from './steps/StepWelcome';
import { StepDomainVerify } from './steps/StepDomainVerify';
import { StepSubscribe } from './steps/StepSubscribe';
import { StepPayment } from './steps/StepPayment';
import { StepProfile } from './steps/StepProfile';
import { StepPersona } from './steps/StepPersona';
import { StepBrand } from './steps/StepBrand';
import { StepActivation } from './steps/StepActivation';
// ADR-073 Amendment 2 (A2.2) — BOW bookend renderers.
import { BowWelcomeStep } from './steps/BowWelcomeStep';
import { BowCompleteStep } from './steps/BowCompleteStep';
// ADR-078 §4 — B2S conversion renderer.
import { B2sCompleteStep } from './steps/B2sCompleteStep';
// Phase 5c.5 (ADR-030 D3) — partner-apply journey step renderers.
import { PartnerWelcomeStep } from './steps/partner/PartnerWelcomeStep';
import { PartnerTermsStep } from './steps/partner/PartnerTermsStep';
import { PartnerPrefillConfirmStep } from './steps/partner/PartnerPrefillConfirmStep';
import { PartnerBusinessDetailStep } from './steps/partner/PartnerBusinessDetailStep';
import { PartnerSubmitStep } from './steps/partner/PartnerSubmitStep';
import { PartnerStatusStep } from './steps/partner/PartnerStatusStep';

export interface AnalysisResult {
  accountId: string;
  domainUrl: string;
  heroHeadline: string;
  placeNoun: string;
  audienceNoun: string;
  funnel: Array<{ icon: string; label: string; description: string }>;
  brandTone: string;
  analysedAt: string;
}

export interface StepProps {
  journeyId: JourneyId;
  accountId?: string;
  domainToken?: string;
  actor: 'subscriber' | 'nexa-admin';
  isRequired: boolean;
  data: Record<string, unknown>;
  analysisResult?: AnalysisResult | null;
  onNext: (stepData: Record<string, unknown>) => void;
  /**
   * Jump directly to an earlier step in this journey (no-op if the step is not
   * in the journey). Used by completion steps to deep-link a recoverable error
   * back to the editable field — e.g. BowCompleteStep sending a duplicate-email
   * or duplicate-domain failure back to the profile / domain-verify step rather
   * than dead-ending at the final confirmation (OBW UAT r2 #4/#7).
   */
  onJumpToStep: (stepId: ObwStepId) => void;
  /** PL22 — the id of the step this journey advances to next (undefined on the
   *  last step, AFTER any skipSteps elision). A step labels its forward button by
   *  the REAL next step — e.g. the Subscribe step reads "Continue to payment"
   *  only when `payment` actually follows, and "Continue" when payment was elided
   *  (card on file) and the snippet/activation step is next. */
  nextStepId?: ObwStepId;
  /** ADR-077 A1.3 — exit the journey immediately (mount routes via onExit). The
   *  step signals; the mount routes (L1 severance). Kept in sync with the staff
   *  §9 fork; the subscriber POW fork is mount-level so no subscriber step uses
   *  it today, but the engine copies stay identical. Optional: not every step
   *  uses it, and test harnesses may omit it. */
  requestExit?: () => void;
  /** ADR-124 I3 — the residency step's mount data + confirm port (see OBWWizardProps). */
  residency?: ResidencyContext;
  /** ADR-073 Am.5 §A5.6 — the payment step's confirm port, when its button is the commitment. */
  payment?: PaymentPort;
  /**
   * How the mount presents the journey (see OBWWizardProps.presentation). A step may condense its
   * content for `embedded` — DATA from the mount, never a sniff of which mount it is (ADR-076 L5).
   */
  presentation?: 'overlay' | 'embedded';
}

/** Why the wizard exited without completing (L1 — the mount routes on this). */
export type ObwExitReason = 'close' | 'rollback';

/**
 * PL32 — the outcome of a mount-provided domain mint (createsDomain journeys).
 * The MOUNT owns the API call + auth (L1/severance); the engine only asks it to
 * create the verified domain and hands back the minted token, or a friendly
 * failure the engine surfaces in its in-modal error panel (the engine owns no
 * toast — ADR-076 L2).
 */
export type CreateDomainOutcome =
  | { ok: true; domainToken: string }
  | { ok: false; message: string };

/** Emitted to the mount when the journey completes (L1). The mount decides the
 *  destination; the engine carries no route. `stepData` is the merged data the
 *  journey collected (including any result a completion step passed via onNext —
 *  e.g. BowCompleteStep's provisioned accountId / customerNumber). */
export interface ObwCompletionResult {
  journeyId: JourneyId;
  accountId: string;
  stepData: Record<string, unknown>;
}

interface OBWWizardProps {
  journeyId: JourneyId;
  accountId: string;
  domainToken?: string;
  initialPlan?: 'T1' | 'T2' | 'T3';
  initialPromo?: string;
  actor?: 'subscriber' | 'nexa-admin';
  /**
   * ADR-077 §2 — the verified-identity signal the engine asserts its trust
   * against at entry. The mount ASSESSES the live session and passes the result;
   * the engine does not blindly trust that a mount satisfied auth — it checks
   * (§4). Defaults to true so existing post-auth mounts are unaffected; an
   * unauthenticated mount that fails to pass this is refused (§2.3).
   */
  authenticated?: boolean;
  /**
   * ADR-073 Am.5 §A5.6 — the FEP's verdict for the PUBLIC SOW mount (ADR-080 §3): trust established
   * BY PAYMENT, not by a session. The engine's trust gate admits an unauthenticated entry carrying it
   * (subscriber actor only — `assertTrust`). Only the public sign-up embed passes it, read from
   * `assessSowMount()`, never hand-set.
   */
  fepTrust?: 'payment';
  /** Pre-seeded step data (e.g. from existing Account record for manage-account,
   *  or the BOW launch token + consultant prefill for the account-less BOW). */
  initialStepData?: Partial<Record<ObwStepId, Record<string, unknown>>>;
  /**
   * PL3 (UAT iter-2) — steps to elide from this journey's configured sequence at
   * mount time, computed by the MOUNT from live data (e.g. skip `payment` when a
   * card is already on file). This is data-driven (no journeyId sniffing here —
   * L5 severance): the engine simply filters its step list. A skipped step is
   * removed entirely, so it is never rendered, required, or counted in progress.
   */
  skipSteps?: ObwStepId[];
  /**
   * #3 — the step this mount should OPEN at (resume restoration).
   *
   * The engine had no resume: it mounted at `useState(0)` unconditionally, so a
   * subscriber who left part-way through and came back — by the requiredAction
   * gate, a reopened email, or simply re-entering — restarted at Step 1 of 8
   * even though every completed step was already persisted server-side. The
   * MOUNT owns the read (it holds auth + the portal API — L1/L5 severance) and
   * supplies the resolved step; the engine just starts there.
   *
   * Semantics are "open AT this step", not "resume after it" — mounts translate
   * the persisted last-COMPLETED step via `resolveResumeStep` (assess.ts), which
   * owns that off-by-one. A step outside this journey's effective sequence
   * (unknown, or elided by `skipSteps`) falls back to the start.
   */
  initialStepId?: ObwStepId;
  /**
   * PL32 — for a `createsDomain` journey (add-second-domain), the MOUNT supplies
   * the domain-mint call (it owns auth + the portal API client). The engine
   * invokes it at `domain-verify` with the verified URL and advances only on
   * success — capturing the returned token so the snippet step can render the
   * embed. Omitted by mounts/journeys that don't create a domain (and by test
   * harnesses); a createsDomain journey without it simply doesn't mint.
   */
  onCreateDomain?: (url: string) => Promise<CreateDomainOutcome>;
  /**
   * ADR-107 §2.4 — PERSIST ONE STEP'S ANSWERS. The mount owns auth, so the mount
   * owns the call; the engine only says WHAT to persist and WHEN.
   *
   * ⛔ THIS REPLACES THE ENGINE'S OWN `fetch`, WHICH 401'd IN PRODUCTION FOR
   * MONTHS (see the module header). Subscriber mounts implement it with
   * `portalApi()` (Bearer idToken). The account-less staff mount omits it — its
   * journeys carry no accountId and persist nothing by design (ADR-107 §2.5).
   *
   * Best-effort by contract: a rejected promise must not break the journey, so
   * the engine swallows failures here exactly as before — but it now swallows a
   * failure the mount can SEE and log, rather than one nothing could observe.
   */
  onPersistStep?: (stepId: ObwStepId, data: Record<string, unknown>) => Promise<void>;
  /**
   * ADR-107 §2.4 — server-side rollback for `exitPolicy: 'rollback'` journeys.
   *
   * ⚠ NOT OPTIONAL IN EFFECT FOR THE SIGN-UP JOURNEY. Rollback releases the
   * PENDING Domain the journey minted at `domain-verify`; without it an abandoned
   * sign-up strands that row and the uniqueness guard answers the visitor's
   * second attempt with 409 — locking them out of their own hostname
   * permanently. The mint and the release are a PAIR (PR #313), and the release
   * half has never once executed because it went through the engine's
   * unauthenticated fetch. A `rollback` journey mounted without this port is a
   * mount defect.
   */
  onRollback?: () => Promise<void>;
  /**
   * ADR-107 §2.4 — website-AI analysis for `autoFill: 'website-ai'` journeys.
   * Also 401'd in production, so autofill has never run for any subscriber.
   * Returns null when analysis is unavailable; pre-fill is an optimisation and
   * its absence never blocks a step.
   */
  onAnalyse?: (domainUrl: string) => Promise<AnalysisResult | null>;
  /**
   * ADR-107 §2.1 — the SERVER-SIDE evidence the validators need but the engine
   * cannot see from in-session answers alone: how many Domain rows the account
   * owns, its recorded tier, its coarse lifecycle state.
   *
   * ⭐ `domainCount` IS WHY THIS PROP EXISTS. `domain-verify` is validated by the
   * Domain row the platform MINTED, never by the URL the user TYPED — accepting
   * the answer would let a failed/duplicate mint pass as done and reproduce the
   * silent half of the #313 defect (an account finishing setup owning nothing).
   * The engine tracks its own mint at runtime (see `mintedDomain`), so a fresh
   * journey validates correctly without the mount re-reading.
   */
  serverEvidence?: Pick<ObwEvidence, 'tier' | 'setupState' | 'domainCount' | 'provisioned'>;
  /** L1 — the mount owns completion routing. Called when the journey finishes. */
  onComplete: (result: ObwCompletionResult) => void;
  /** L1 — the mount owns exit routing. Called when the user exits early
   *  (after the in-app confirm panel, and after rollback for rollback journeys). */
  onExit: (reason: ObwExitReason) => void;
  /**
   * Force the in-app exit confirm even on a `save` journey with a committed account.
   *
   * For a mount reached by a **blocking `requiredAction` redirect**, where `onExit`
   * cannot go "back" (the previous page is the gate that redirected here) and must
   * therefore end the session. Without it, ✕ on such a mount fires `onExit('close')`
   * silently and the user is signed out with no warning; with the old exit
   * destination it instead bounced off the gate and remounted the wizard at step 1,
   * which is the "no way out of the modal" trap this flag exists to close.
   * Default false — ordinary in-portal mounts are unaffected.
   */
  confirmOnExit?: boolean;
  /**
   * ADR-077 §2.3 — refusal handler. Fired (once) when the engine's entry
   * assessment finds the entry's trust insufficient for this journey. The mount
   * owns the shared toast (L2) and routing (L1), so it surfaces the
   * insufficient-permission message and navigates away. If omitted, the engine
   * still halts and renders an in-overlay refusal — it NEVER runs the journey.
   */
  onRefuse?: (message: string) => void;
  /**
   * ADR-124 I3 — what the `residency` step needs from its MOUNT: the served country → zone map,
   * a location pre-fill, the stored answer for an account-bearing journey (read-only — D1), and
   * the `confirm` port the step runs when an account-less journey's answer is confirmed.
   *
   * ⚠ DATA AND A PORT, NEVER A FETCH. The engine and its steps hold no transport (ADR-107 §2.4);
   * the mount loads the map and owns every call. `map` also feeds the `residency` validator's
   * evidence (`countryZones`), so the same served map both renders the choice and judges it.
   */
  residency?: ResidencyContext;
  /**
   * ADR-073 Am.5 §A5.6 — the `payment` step's confirm port, for a mount on which pressing Pay IS the
   * commercial commitment (the public sign-up: payment-confirm creates the account). Data and a
   * port, never a fetch — the residency contract (payment.ts). Omitted ⇒ the step is unchanged.
   */
  payment?: PaymentPort;
  /**
   * ADR-073 Am.5 §A5.6 — `embedded`: the journey IS the page (the public sign-up's iframe inside the
   * marketing site's modal shell, ADR-080 Am.2), not an overlay floating over a portal. The shell
   * owns the frame and its close control, so the engine drops its own backdrop and ✕, sizes to the
   * frame, and steps condense to fit it without scrolling (Jeff: "No scrolling, No opportunity for
   * confusion"). Default `overlay` — every portal mount is unchanged.
   */
  presentation?: 'overlay' | 'embedded';
  /**
   * ADR-073 Am.5 §A5.6 — the steps up to AND including this one are DONE FOR GOOD: Back never
   * returns to them and no step can jump to them. The public sign-up sets it to `payment` once the
   * payment confirms — after that, the pre-payment steps describe a sign-up that has become an
   * account (its lead is gone), and walking back to Pay would ask to pay again. Data from the mount,
   * changeable mid-journey without moving the step the customer is on. Omitted ⇒ no floor.
   */
  lockedThroughStepId?: ObwStepId;
}

export function OBWWizard({
  journeyId,
  accountId,
  domainToken: initialDomainToken,
  initialPlan,
  initialPromo,
  actor = 'subscriber',
  authenticated = true,
  fepTrust,
  initialStepData,
  skipSteps,
  initialStepId,
  onCreateDomain,
  onPersistStep,
  onRollback,
  onAnalyse,
  serverEvidence,
  onComplete,
  onExit,
  onRefuse,
  confirmOnExit = false,
  residency,
  payment,
  presentation = 'overlay',
  lockedThroughStepId,
}: OBWWizardProps) {
  const journey = JOURNEY_MATRIX[journeyId];
  // PL3 — the effective step sequence: the journey's configured steps minus any
  // the mount elided from live data (skipSteps). All step navigation below is
  // driven by `steps`, never `journey.steps`, so a skipped step is invisible to
  // the engine (never rendered, indexed, counted, or treated as required).
  const steps =
    skipSteps && skipSteps.length > 0
      ? journey.steps.filter((s) => !skipSteps.includes(s))
      : journey.steps;

  // ADR-077 §2.1 — assessment is the engine's OWN first action at entry, before
  // any modal renders, any data call fires, or any step logic runs. It is
  // intrinsic and unskippable: no mount can run a journey above its assessed
  // trust. The verdict is computed here, enforced by the early return below, and
  // emitted to the mount's toast via onRefuse. (Resolution of WHICH journey is
  // the mount's job — resolveSubscriberEntry; this is the trust ENFORCEMENT that
  // backstops every mount, §2.3.)
  const trust = assertTrust(journeyId, { authenticated, actor, fepTrust });

  // Merge initial plan/promo into subscribe step seed data.
  //
  // ⚠ A RESUMED ANSWER OUTRANKS THE URL PARAM. `?plan=` is sticky — nothing strips it
  // (assess.ts documents the same stickiness for entry resolution) — so it survives an
  // exit and is still on the URL when the subscriber re-enters. Seeded LAST, as it was,
  // it silently overwrote the tier they had actually chosen in the wizard with the one
  // they first arrived with: the answers were resumed and then discarded a line later.
  // The param is the fresh-entry default; a persisted answer is a later statement by
  // the same user and wins. A blank/absent persisted value falls back to the param, so
  // a fresh sign-up (`initialStepData` undefined) behaves exactly as before.
  const resumedSubscribe = initialStepData?.subscribe ?? {};
  const kept = (key: string) => {
    const v = resumedSubscribe[key];
    return v !== undefined && v !== null && v !== '';
  };
  const seedData: Record<ObwStepId, Record<string, unknown>> = {
    ...(initialStepData ?? {}),
    subscribe: {
      ...resumedSubscribe,
      // Applied AFTER the resumed answers but only where the resumed value is blank —
      // spreading the params first would let a persisted `promoCode: null` (which
      // StepSubscribe emits whenever no promo is applied) blank out `?promo=`.
      ...(initialPlan && !kept('selectedTier') ? { selectedTier: initialPlan } : {}),
      ...(initialPromo && !kept('promoCode') ? { promoCode: initialPromo } : {}),
    },
  } as Record<ObwStepId, Record<string, unknown>>;

  // #3 — resume restoration. Computed in the initialiser (once, at mount) against
  // the EFFECTIVE `steps` list, so a resumed step that the mount elided via
  // skipSteps degrades to the start rather than indexing to -1. Deliberately not
  // an effect: re-seeding after mount would yank a user out of the step they are
  // standing in whenever the mount re-rendered with a fresher read.
  const [stepIndex, setStepIndex] = useState(() => {
    const i = initialStepId ? steps.indexOf(initialStepId) : -1;
    return i >= 0 ? i : 0;
  });
  const [stepData, setStepData] = useState<Record<ObwStepId, Record<string, unknown>>>(seedData);
  // PL32 — mutable so a createsDomain journey can capture the token minted at
  // domain-verify (an existing-domain journey seeds it once from the mount).
  const [domainToken, setDomainToken] = useState<string | undefined>(initialDomainToken);
  /**
   * ADR-107 — did THIS session successfully mint a Domain row? Distinct from
   * `domainToken`, which an existing-domain mount seeds without any mint having
   * happened. Only a confirmed server write sets this, and only it may be counted
   * as evidence for the `domain-verify` validator (see `currentEvidence`).
   */
  const [mintedDomain, setMintedDomain] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fatalError, setFatalError] = useState('');
  // L2 — in-app exit confirmation (replaces window.confirm). Shown inside
  // .obw-overlay; the mount routes when onExit fires.
  const [confirmingExit, setConfirmingExit] = useState(false);

  // ADR-077 §2.3 — on insufficient trust, notify the mount once so it can raise
  // the shared toast (L2) and route away (L1). The engine never toasts/routes
  // itself; it halts (the early return below) and hands the refusal to the mount.
  useEffect(() => {
    if (!trust.ok && trust.message) onRefuse?.(trust.message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trust.ok]);

  const currentStepId = steps[stepIndex];

  // Flatten a step-data map into the merged shape steps + the mount consume.
  function flatten(map: Record<ObwStepId, Record<string, unknown>>): Record<string, unknown> {
    return Object.values(map).reduce<Record<string, unknown>>((acc, d) => ({ ...acc, ...d }), {});
  }
  // Collect all step data merged for passing to step renderers.
  function getStepData(): Record<string, unknown> {
    return flatten(stepData);
  }
  /**
   * ADR-107 §2.1 — the evidence bundle as the ENGINE sees it right now: the
   * mount's server-side read, plus everything captured in this session, plus the
   * engine's own knowledge of the Domain it just minted.
   *
   * ⭐ `mintedDomain` IS NOT A SHORTCUT AROUND SERVER TRUTH. `domain-verify` is
   * validated by a Domain ROW, and the mount's `domainCount` was read before this
   * journey minted one — so without this the engine would refuse to complete a
   * journey whose domain it had just successfully created, and would bounce the
   * user back to `domain-verify` forever. The mint is a confirmed server write
   * (it returned a token); counting it is reporting a fact, not assuming one.
   */
  function currentEvidence(
    map: Record<ObwStepId, Record<string, unknown>>,
  ): ObwEvidence {
    const base = serverEvidence ?? { domainCount: 0, provisioned: false };
    return {
      answers: map,
      tier: base.tier ?? null,
      setupState: base.setupState ?? null,
      domainCount: (base.domainCount ?? 0) + (mintedDomain ? 1 : 0),
      provisioned: base.provisioned ?? false,
      /**
       * A property of the MOUNT, not of any read (see validators.ts). `false` for
       * the sign-up SOW and every in-portal journey — they carry a real account
       * id, and their owner's email was captured at the public form long before
       * the wizard ran, so the profile step is unchanged for them.
       *
       * `true` for the ONE account-less mount on this surface: the /onboard/bow
       * beta landing, which mounts with accountId '' and provisions from these
       * answers exactly as the staff fork does — so it needs the email for the
       * same reason, and BowCompleteStep already refuses without it.
       */
      accountless: !accountId,
      // ADR-124 — the served map judges the `residency` answer on an account-less journey.
      countryZones: residency?.map?.countries,
    };
  }

  /**
   * L1 — completion is the mount's to route. The engine emits the result.
   *
   * ⛔ VALIDATOR-GATED, NOT INDEX-GATED (ADR-107 §2.2). Completion used to mean
   * "the step index reached the end of the array", which is a statement about the
   * UI, not about the account. A journey entered mid-way (the old `setupState`
   * ladder routed a step-less account straight to Payment) would walk from there
   * to the end and report DONE with `domain-verify` never run — the account
   * finishing setup owning zero Domain rows, the silent half of the #313 defect.
   *
   * If any required validator still fails, the engine navigates TO that step
   * instead of completing. It never completes with work outstanding, and it never
   * loops: the target is always a step that exists in this journey's effective
   * sequence, and reaching it is the only way to satisfy the validator that names
   * it.
   */
  function complete(map: Record<ObwStepId, Record<string, unknown>>) {
    const outstanding = firstIncompleteRequiredStep(steps, journey.required, currentEvidence(map));
    if (outstanding) {
      const idx = steps.indexOf(outstanding);
      if (idx >= 0) {
        setStepIndex(idx);
        return;
      }
      // The failing step is not in this journey's EFFECTIVE sequence — the mount
      // elided it via skipSteps. Elision is the mount asserting the requirement is
      // already met by other means (a card already on file), so honour it and
      // complete rather than stranding the user on a step that is not rendered.
    }
    onComplete({ journeyId, accountId, stepData: flatten(map) });
  }

  // AI website analysis after domain-verify, for website-ai journeys — through
  // the MOUNT's port (ADR-107 §2.4). The engine's own fetch 401'd, so this has
  // never once run in production.
  const triggerAnalysis = useCallback(
    async (domainUrl: string) => {
      if (journey.autoFill !== 'website-ai' || !onAnalyse) return;
      setAnalysisLoading(true);
      try {
        const result = await onAnalyse(domainUrl);
        if (result) setAnalysisResult(result);
      } catch {
        // Non-fatal — analysis is pre-fill only; the user can fill manually.
      } finally {
        setAnalysisLoading(false);
      }
    },
    [journey.autoFill, onAnalyse],
  );

  async function persistStep(stepId: ObwStepId, data: Record<string, unknown>) {
    // No port ⇒ nothing to persist (the account-less staff/BOW journeys). A legitimate mount, not a
    // failure — see the `onPersistStep` prop doc and ADR-107 §2.5.
    //
    // ⭐ THE PORT'S PRESENCE IS THE MOUNT'S STATEMENT, NOT THE ACCOUNT ID'S (ADR-073 Am.5 §A5.6). This
    // used to also return when `accountId` was empty, which was right while every port wrote to an
    // account. The public sign-up is account-less until payment yet still has somewhere to persist —
    // the SignupLead, its pre-payment resume store — so its mount supplies a port with no account
    // behind it. Every OTHER account-less mount supplies no port (`obwPorts('')` returns null), so
    // for them nothing changes.
    if (!onPersistStep) return;
    try {
      await onPersistStep(stepId, data);
    } catch {
      // Best-effort by contract: a persistence hiccup must not strand the user
      // mid-journey. The MOUNT sees and logs the failure — which is the whole
      // difference from the old engine-owned fetch, whose failures nothing could
      // observe.
    }
  }

  // Server-side rollback for rollback journeys (sign-up, OBW-012) — through the
  // mount's port. Releases the PENDING Domain the journey minted; see the
  // `onRollback` prop doc for why the mint and this release are a pair.
  async function rollback() {
    if (journey.exitPolicy !== 'rollback' || !onRollback) return;
    try {
      await onRollback();
    } catch {
      // Best-effort — the mount routes regardless via onExit('rollback').
    }
  }

  async function handleStepNext(newData: Record<string, unknown>) {
    setSaving(true);
    const merged = { ...stepData[currentStepId], ...newData };
    const nextStepData = { ...stepData, [currentStepId]: merged };
    setStepData(nextStepData);

    // PL32 — createsDomain journeys (add-second-domain) MINT the Domain here, at
    // domain-verify, via the mount-provided call (which owns auth + the API
    // client). We must succeed BEFORE advancing: the write persists the domain
    // AND yields the token the snippet (activation) step renders. A duplicate or
    // failure keeps the user on this step with a friendly in-modal message (the
    // engine owns no toast — ADR-076 L2). Only fires when we don't already hold
    // a token (an existing-domain mount seeds one).
    if (
      currentStepId === 'domain-verify' &&
      journey.createsDomain &&
      onCreateDomain &&
      !domainToken &&
      typeof newData.domainUrl === 'string' &&
      newData.domainUrl
    ) {
      const outcome = await onCreateDomain(newData.domainUrl);
      if (!outcome.ok) {
        setSaving(false);
        setFatalError(outcome.message);
        return; // stay on domain-verify — do not advance
      }
      setDomainToken(outcome.domainToken);
      // A confirmed server write — the Domain row now exists, so the
      // `domain-verify` validator may count it (ADR-107 §2.1).
      setMintedDomain(true);
    }

    // Extract domainToken from domain-verify step (issued server-side, but we also store what we have)
    if (currentStepId === 'domain-verify' && newData.domainUrl) {
      void triggerAnalysis(newData.domainUrl as string);
    }

    await persistStep(currentStepId, merged);
    setSaving(false);

    if (stepIndex < steps.length - 1) {
      setStepIndex((i) => i + 1);
    } else {
      // Last step — emit completion to the mount (L1). No route here.
      complete(nextStepData);
    }
  }

  // The first index Back may reach (see `lockedThroughStepId`). 0 when nothing is locked.
  const lockedIdx = lockedThroughStepId ? steps.indexOf(lockedThroughStepId) : -1;
  const backFloor = lockedIdx >= 0 ? lockedIdx + 1 : 0;

  function handleBack() {
    if (stepIndex > backFloor) setStepIndex((i) => i - 1);
  }

  // Jump to a named step (recoverable-error deep-link — OBW UAT r2 #4/#7).
  // No-op if the step is not part of this journey, or is locked (done for good).
  function goToStep(stepId: ObwStepId) {
    const idx = steps.indexOf(stepId);
    if (idx >= backFloor) setStepIndex(idx);
  }

  // Skip an optional (non-required) step without persisting new data.
  function handleSkip() {
    if (stepIndex < steps.length - 1) {
      setStepIndex((i) => i + 1);
    } else {
      complete(stepData);
    }
  }

  // ✕ close — L1/L2/L5. The engine never routes and never sniffs journeyId.
  // Whether exit discards in-flight work is data-driven: rollback journeys
  // discard server-side progress; an account-less journey (BOW / card-first SOW,
  // accountId === '') holds everything in memory and loses it on exit. Both show
  // the in-app confirm panel. A 'save' journey with a committed account exits
  // straight away (progress is already persisted) — the mount routes.
  // `confirmOnExit` lets a BLOCKING mount opt into the confirm panel. A mount reached
  // by a `requiredAction` redirect cannot return the user whence they came — that page
  // is the very gate that bounced them here — so its exit leaves the session entirely.
  // That is a big enough consequence to confirm, even on a `save` journey whose
  // progress is already persisted.
  const exitDiscardsProgress = journey.exitPolicy === 'rollback' || !accountId || confirmOnExit;

  function handleClose() {
    if (exitDiscardsProgress) {
      setConfirmingExit(true);
      return;
    }
    onExit('close');
  }

  // Confirm-exit (in-app panel). Rollback journeys roll back server-side first,
  // then the mount routes via onExit. No native dialog, no window.close.
  async function confirmExit() {
    setConfirmingExit(false);
    if (journey.exitPolicy === 'rollback') {
      await rollback();
      onExit('rollback');
    } else {
      onExit('close');
    }
  }

  // ADR-077 §2.3 — REFUSAL is a first-class outcome of resolution. If the entry's
  // assessed trust is insufficient for this journey, the engine halts here: no
  // step renders, no data call fires, the privileged path never runs. The mount
  // (notified via onRefuse above) raises the shared toast and routes away.
  const overlayClass = presentation === 'embedded' ? 'obw-overlay obw-embedded' : 'obw-overlay';
  if (!trust.ok) {
    return (
      <div className={overlayClass} role="dialog" aria-modal="true" aria-label="Permission required">
        <div className="obw-modal">
          <div className="obw-body">
            <div className="portal-wizard-error" role="alert">
              <span className="portal-wizard-error-code">Permission required</span>
              {trust.message}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (fatalError) {
    return (
      <div className={overlayClass} role="dialog" aria-modal="true" aria-label="Setup error">
        <div className="obw-modal">
          <div className="obw-body">
            <div className="portal-wizard-error" role="alert">
              <span className="portal-wizard-error-code">Error</span>
              {fatalError}
            </div>
            <button type="button" className="obw-btn-secondary" onClick={() => setFatalError('')}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isRequired = journey.required.includes(currentStepId);
  const mergedData = getStepData();

  const stepProps: StepProps = {
    journeyId,
    accountId,
    domainToken,
    actor,
    isRequired,
    data: mergedData,
    analysisResult,
    // PL22 — the next step in the effective (post-skip) sequence, so a step can
    // label its forward button by what actually comes next. Undefined on the last.
    nextStepId: steps[stepIndex + 1],
    onNext: (d) => void handleStepNext(d),
    onJumpToStep: goToStep,
    requestExit: () => onExit('close'),
    residency,
    payment,
    presentation,
  };

  function renderStep() {
    switch (currentStepId) {
      case 'welcome':
        return <StepWelcome {...stepProps} />;
      case 'residency':
        return <StepResidency {...stepProps} />;
      case 'domain-verify':
        return <StepDomainVerify {...stepProps} />;
      case 'subscribe':
        return <StepSubscribe {...stepProps} />;
      case 'payment':
        return <StepPayment {...stepProps} />;
      case 'profile':
        return <StepProfile {...stepProps} />;
      case 'persona':
        return <StepPersona {...stepProps} analysisResult={analysisResult} />;
      case 'brand':
        return <StepBrand {...stepProps} analysisResult={analysisResult} />;
      case 'activation':
        return <StepActivation {...stepProps} />;
      // ADR-073 Amendment 2 (A2.2) — BOW bookend modals.
      case 'bow-welcome':
        return <BowWelcomeStep {...stepProps} />;
      case 'bow-complete':
        return <BowCompleteStep {...stepProps} />;
      // ADR-078 §4 — B2S conversion bookend.
      case 'b2s-complete':
        return <B2sCompleteStep {...stepProps} />;
      // Phase 5c.5 (ADR-030 D3) — partner-apply journey
      case 'pa-welcome':
        return <PartnerWelcomeStep {...stepProps} />;
      case 'pa-terms':
        return <PartnerTermsStep {...stepProps} />;
      case 'pa-prefill':
        return <PartnerPrefillConfirmStep {...stepProps} />;
      case 'pa-business':
        return <PartnerBusinessDetailStep {...stepProps} />;
      case 'pa-submit':
        return <PartnerSubmitStep {...stepProps} />;
      case 'pa-status':
        return <PartnerStatusStep {...stepProps} />;
      default:
        return <div>Unknown step: {currentStepId as string}</div>;
    }
  }

  const totalSteps = steps.length;
  const canSkip = !isRequired && stepIndex < totalSteps - 1;

  const embedded = presentation === 'embedded';
  return (
    <div
      className={overlayClass}
      role="dialog"
      aria-modal="true"
      aria-label={`Onboarding — step ${stepIndex + 1} of ${totalSteps}`}
      data-presentation={presentation}
    >
      <div className="obw-modal">
        {/* Close (✕) — exit per journey policy. Embedded: the SHELL owns close (its own ✕ in the
            marketing site's modal header) — a second ✕ inside the frame is one control too many. */}
        {!embedded && (
          <button
            type="button"
            className="obw-close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        )}

        {/* Dot progress + caption */}
        <div className="obw-dots" role="presentation">
          {steps.map((stepId, i) => {
            let cls = 'obw-dot';
            if (i < stepIndex) cls += ' done';
            else if (i === stepIndex) cls += ' active';
            return <span key={stepId} className={cls} title={MASTER_STEP_LABELS[stepId]} />;
          })}
        </div>
        <p className="obw-progress-caption" aria-live="polite">
          Step {stepIndex + 1} of {totalSteps} — {MASTER_STEP_LABELS[currentStepId]}
        </p>

        {/* Analysis loading notice */}
        {analysisLoading && (
          <div className="obw-analysis-loading" aria-live="polite">
            Analysing your website…
          </div>
        )}

        {/* Step body — each step renders its own title/content + forward button */}
        <div className="obw-body">
          {saving && <div className="sr-only" aria-live="polite">Saving…</div>}
          {renderStep()}
        </div>

        {/* Footer — Back (left) + Skip (right, optional steps only) */}
        <div className="obw-footer">
          <div className="obw-footer-left">
            {stepIndex > backFloor && (
              <button type="button" className="obw-btn-secondary" onClick={handleBack}>
                ← Back
              </button>
            )}
          </div>
          <div className="obw-footer-right">
            {canSkip && (
              <button type="button" className="obw-skip" onClick={handleSkip}>
                Skip — I'll do this later
              </button>
            )}
          </div>
        </div>

        {/* L2 — in-app exit confirmation (replaces window.confirm). Rendered
            inside the modal so focus stays in-app; never a native dialog. */}
        {confirmingExit && (
          <div className="obw-confirm-scrim" role="alertdialog" aria-modal="true" aria-label="Confirm exit">
            <div className="obw-confirm-panel">
              <h3 className="obw-confirm-title">Exit setup?</h3>
              <p className="obw-confirm-body">
                {journey.exitPolicy === 'rollback'
                  ? 'Your progress on this domain will be discarded.'
                  : confirmOnExit
                    ? // The blocking-mount case: setup must finish before the dashboard
                      // opens, so leaving ends the session. Say so plainly rather than
                      // signing the user out with no warning.
                      'You’ll be signed out. Your account is saved — sign back in any time to finish setting up.'
                    : 'Any details entered will be discarded.'}
              </p>
              <div className="obw-confirm-actions">
                <button type="button" className="obw-btn-secondary" onClick={() => setConfirmingExit(false)}>
                  Keep editing
                </button>
                <button type="button" className="obw-btn-danger" onClick={() => void confirmExit()}>
                  Exit & discard
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
